import { _ } from '@sveltia/i18n';
import { isObject } from '@sveltia/utils/object';

import { unsupportedBackends, validBackendNames } from '$lib/services/backends';
import { gitBackendServices } from '$lib/services/backends/git/services';
import { normalizeRootDir } from '$lib/services/backends/root-dir';
import { warnDeprecation } from '$lib/services/config/deprecations';
import { addMessage, checkUnsupportedOptions } from '$lib/services/config/parser/utils/validator';
import { isHttpURL, makeLink } from '$lib/services/utils/string';
import {
  getPublishMode,
  isWorkflowConfigured,
  isWorkflowMode,
} from '$lib/services/workflow/config';

/**
 * @import { CmsConfig, GitBackend } from '$lib/types/public';
 * @import { ConfigParserCollectors, UnsupportedOption } from '$lib/types/private';
 */

/**
 * Unsupported options for Relation fields.
 * @type {UnsupportedOption[]}
 */
const UNSUPPORTED_OPTIONS = [
  // Sveltia CMS always uses GraphQL for Git backends, so this option is not applicable.
  { type: 'warning', prop: 'use_graphql', strKey: 'unsupported_ignored_option' },
];

const UNSUPPORTED_BACKEND_DOC_URL =
  'https://sveltiacms.app/en/docs/migration/netlify-decap-cms#features-not-to-be-implemented';

const UNSUPPORTED_BACKEND_SUGGESTION_URL =
  'https://sveltiacms.app/en/docs/backends#supported-backends';

const AUTH_DOC_URL = 'https://sveltiacms.app/en/docs/backends/BACKEND_NAME#authentication';
/**
 * Backend names whose documentation page has a different slug.
 * @type {Record<string, string>}
 */
const BACKEND_DOC_SLUGS = { gitea: 'gitea-forgejo' };
const OPEN_AUTHORING_DOC_URL = 'https://sveltiacms.app/en/docs/workflows/open';
/**
 * Backend options that hold the URL of an API or authentication server and have to be absolute. A
 * relative or scheme-less URL would either make the backend throw while it’s being set up, leaving
 * the user unable to sign in without any explanation, or send the requests to the CMS origin.
 */
const URL_OPTIONS = ['api_root', 'graphql_api_root', 'base_url'];
/**
 * Regular expression matching a path segment Git doesn’t allow in a branch name: one starting with
 * a dot or ending with `.lock`, or one containing a control character, a space, any of `~^:?*[\`
 * or `@{`.
 * @see https://git-scm.com/docs/git-check-ref-format
 */
// eslint-disable-next-line no-control-regex
const INVALID_BRANCH_SEGMENT_REGEX = /^\.|\.lock$|[\x00-\x20\x7f~^:?*[\\]|@\{/;

/**
 * Check whether the given directory path can be part of a Git branch name.
 * @param {string} path Normalized directory path.
 * @returns {boolean} Result.
 */
const isValidBranchPath = (path) =>
  path.split('/').every((segment) => !INVALID_BRANCH_SEGMENT_REGEX.test(segment));

/**
 * Parse and validate the backend configuration from the site config.
 * @param {CmsConfig} cmsConfig Raw CMS configuration.
 * @param {ConfigParserCollectors} collectors Collectors.
 * @throws {Error} If there is an error in the backend config.
 */
export const parseBackendConfig = (cmsConfig, collectors) => {
  const { backend } = cmsConfig;
  const { errors, warnings } = collectors;

  if (!isObject(backend)) {
    errors.add(_('config.error.missing_backend'));

    return;
  }

  const { name } = backend;

  if (!name) {
    errors.add(_('config.error.missing_backend_name'));

    return;
  }

  if (!validBackendNames.includes(name)) {
    const _backend = unsupportedBackends[name];
    const type = _backend ? (_backend.deprecated ? 'deprecated' : 'known') : 'custom';
    const label = _backend?.label;

    const message = makeLink(
      _(`config.error.unsupported_${type}_backend`, { values: { name: label } }),
      UNSUPPORTED_BACKEND_DOC_URL,
    );

    const suggestion = makeLink(
      _('config.error.unsupported_backend_suggestion'),
      UNSUPPORTED_BACKEND_SUGGESTION_URL,
    );

    errors.add(`${message} ${suggestion}`);

    return;
  }

  const { root_dir: rootDir } = backend;

  if (typeof rootDir === 'string') {
    // The CMS can’t see anything outside the repository, so a root directory that climbs out of it
    // could only ever lead to a confusing error later
    if (rootDir.split('/').includes('..')) {
      errors.add(_('config.error.invalid_root_dir'));
    } else if (isWorkflowConfigured(cmsConfig) && !isValidBranchPath(normalizeRootDir(rootDir))) {
      // Editorial Workflow names its branches after the directory, which Git would refuse
      errors.add(_('config.error.invalid_root_dir_branch'));
    }
  }

  if (name in gitBackendServices) {
    const {
      repo,
      automatic_deployments: autoDeploy,
      auth_methods: authMethods,
      // @ts-ignore GitHub/GitLab only
      auth_type: authType,
      // @ts-ignore GitLab/Gitea only
      app_id: appId,
      open_authoring: openAuthoring,
      // @ts-ignore GitHub only
      auth_scope: authScope,
    } = /** @type {GitBackend} */ (backend);

    if (Array.isArray(authMethods) && !authMethods.length) {
      errors.add(_('config.error.no_auth_methods'));
    }

    // A list of scopes can add `workflow`, which is no use on its own: without a repository scope
    // the CMS can’t write to the repository or a fork of it. The schema only checks each scope
    if (
      name === 'github' &&
      Array.isArray(authScope) &&
      !authScope.some((scope) => scope === 'repo' || scope === 'public_repo')
    ) {
      errors.add(_('config.error.auth_scope_no_repo'));
    }

    const allowTokenAuth = !authMethods || authMethods.includes('token');
    const authDocURL = AUTH_DOC_URL.replace('BACKEND_NAME', BACKEND_DOC_SLUGS[name] ?? name);

    if (repo === undefined) {
      errors.add(_('config.error.missing_repository'));
    } else if (typeof repo !== 'string' || !/^[^/:]+(?:\/[^/:]+)+$/.test(repo)) {
      // A configured but malformed repository is a separate problem from a missing one, and
      // reporting both for the same option only reads as noise
      errors.add(_('config.error.invalid_repository'));
    }

    if (authType === 'implicit') {
      errors.add(makeLink(_('config.error.oauth_implicit_flow'), authDocURL));
    }

    if (name === 'github' && authType === 'pkce') {
      errors.add(makeLink(_('config.error.github_pkce_unsupported'), authDocURL));
    }

    if (name === 'gitlab' && authType === 'pkce' && !appId) {
      errors.add(_('config.error.oauth_no_app_id'));
    }

    if (openAuthoring) {
      // Open Authoring turns each change into a pull request from the contributor’s fork, so it
      // only makes sense on top of Editorial Workflow or Preview Workflow. The site-level option is
      // what counts here: a collection can opt out with its own `publish_mode`, but that only
      // affects maintainers, who write to the configured repository; a contributor’s fork always
      // goes through pull requests
      if (!isWorkflowMode(getPublishMode({ cmsConfig }))) {
        errors.add(makeLink(_('config.error.open_authoring_no_workflow'), OPEN_AUTHORING_DOC_URL));
      }

      // The default scope grants access to every repository the contributor owns, including their
      // private ones. That’s a lot to ask of someone who just wants to fix a typo, and a public
      // repository doesn’t need it. The visibility of the repository isn’t known until someone
      // signs in, so this can’t be decided automatically. GitLab has no equivalent option: its
      // single `api` scope covers everything the CMS does. Gitea/Forgejo scopes are already
      // limited to what the CMS uses
      if (name === 'github' && authScope === undefined) {
        // Warnings are logged to the console for whoever set the CMS up, so this is plain English
        // with a plain URL rather than a localized string with a link in it
        warnings.add(
          'The `open_authoring` option is enabled without `auth_scope`, so contributors are ' +
            'asked for access to all their repositories, including private ones. Set ' +
            '`auth_scope: public_repo` if your repository is public, or `auth_scope: repo` to ' +
            `confirm the broader scope is needed. ${OPEN_AUTHORING_DOC_URL}`,
        );
      }
    }

    // Gitea requires an app ID for OAuth authentication, but also supports token-based sign-in,
    // which doesn’t require one. If no app ID is configured and token auth is allowed (the
    // default), we issue a warning and disable the OAuth Sign In button in the UI — users can still
    // sign in with a token. If token auth is explicitly disabled as well, we issue an error because
    // there is no working sign-in method available.
    // @see https://github.com/sveltia/sveltia-cms/issues/721
    if (name === 'gitea' && !appId) {
      if (allowTokenAuth) {
        warnings.add(_('config.warning.oauth_no_app_id'));
      } else {
        errors.add(_('config.error.oauth_no_app_id'));
      }
    }

    URL_OPTIONS.forEach((option) => {
      const url = /** @type {Record<string, any>} */ (backend)[option];

      // A value of another type is reported against the JSON schema
      if (typeof url === 'string' && !isHttpURL(url)) {
        addMessage({
          strKey: 'invalid_url_option',
          values: { option, url },
          context: { cmsConfig },
          collectors,
        });
      }
    });

    // @todo Remove the option prior to the 1.0 release.
    if (autoDeploy !== undefined) {
      warnDeprecation('automatic_deployments');
    }

    checkUnsupportedOptions({
      UNSUPPORTED_OPTIONS,
      config: backend,
      context: { cmsConfig },
      collectors,
    });
  }
};
